// 데미지 숫자: DOM 풀(화면 동시 최대 MAX_ON_SCREEN = 12). 모양은 클래스로 고정하고 프레임마다 transform·opacity만 바꾼다.
// Ev.Hit에서만 띄운다(영웅 피격 b=2 포함). 혼전에서도 화면이 읽히도록 다음 규칙으로 줄인다.
// - 피해를 준 타격마다 숫자가 뜬다(처치 타격 포함). 한 동작의 몹을 대표 몇 개로 줄이는 규칙은 없다.
// - 같은 대상에 MERGE_MS 안에 들어온 숫자는 합친다(합쳐도 보인다).
// - 화면 상한(MAX_ON_SCREEN)에 걸리면 앞선 동작의 가장 오래된 숫자를 빨리 지워 새 숫자 자리를 만든다.
//   한 동작 하나가 상한보다 많은 몹을 한 번에 맞힌 경우에만 우선순위(치명·각성·스킬 → 큰 값) 순으로 상한까지 띄운다.
// - 일반 타격은 작고 빨리 사라지고, 치명·마무리·스킬·각성만 크게. 영웅 피격은 빨갛게, 상한에 걸려도 밀려나지 않는다.
// - 방패에 막힌 타격(flags&32)은 숫자 대신 작은 회색 '막힘'(한 동작에 하나).
// 이벤트는 onEvent에서 고정 버퍼에 쌓고 update에서 묶어 처리한다(프레임 중 새 객체를 만들지 않는다. 숫자 문자열은 숫자가 생길 때만 만든다).
// HUD 예약 영역(ScreenReserve: 대상 프레임·보스 바)과 겹치는 숫자는 가장 가까운 바깥 자리로 비켜 그린다(숨기지 않는다, COMBAT-FEEL 4장).
import * as THREE from 'three/webgpu';
import { DAMAGE_NUMBERS } from '../shared/config';
import { Ev, type GameEvent } from '../shared/events';
import { AVOID_NONE, avoidReserve, overlapsReserve, type ScreenReserve } from '../ui/screenReserve';

/** 화면 동시 상한(COMBAT-OVERHAUL 목표: 12개 이하). config 값이 더 작으면 그 값을 따른다 */
export const MAX_ON_SCREEN = Math.min(12, DAMAGE_NUMBERS.maxOnScreen);
/** 같은 대상 합산 창(ms) */
export const MERGE_MS = 100;
const STACK_WINDOW_MS = 320;
const STAGE_CAP = 128;

/** Ev.Hit flags(events.ts 주석이 정본) */
const F_CRIT = 1, F_FINISHER = 2, F_SKILL = 4, F_AWAKEN = 8, F_MULTI = 16, F_BLOCKED = 32;

/** 숫자 종류. 숫자가 클수록 우선순위가 높다(PRIO) */
const K_MINOR = 0, K_BLOCK = 1, K_NORMAL = 2, K_BIG = 3, K_AWAKEN = 4, K_CRIT = 5, K_HERO = 6;
const PRIO = [0, 1, 2, 3, 4, 4, 9];
const CLS = ['dn dn-s', 'dn dn-x', 'dn dn-n', 'dn dn-k', 'dn dn-a', 'dn dn-c', 'dn dn-h'];
/** 수명(ms): 일반은 짧게, 큰 숫자는 COMBAT-FEEL 4장 600ms 안팎, 영웅 피격은 조금 더 */
const LIFE = [300, 420, 420, 620, 650, 650, 760];
/** 위로 떠오르는 비율(riseM 배) */
const RISE = [0.5, 0.4, 0.6, 1, 1, 1, 0.7];
/** 등장 펀치 배율(×50): 큰 숫자만 강하게 */
const PUNCH = [4, 4, 8, 20, 22, 22, 18];
const BLOCK_TEXT = '막힘';
/** 불투명도 문자열 표(0..20 → '0'..'1') */
const OPACITY: readonly string[] = Array.from({ length: 21 }, (_, i) => String(i / 20));
/** 종류별 글자 크기(px). 아래 CSS와 비키기 상자 추정이 같은 값을 쓴다 */
const FONT_PX = [15, 15, 19, 30, 34, 38, 28];
/** 글자 폭(글자 크기 배): 숫자·쉼표는 약 0.6em, 한글 '막힘'은 1em */
const CHAR_EM = [0.6, 1, 0.6, 0.6, 0.6, 0.6, 0.6];
/** 좌우 안쪽 여백 합(px): 막힘 6px×2, 치명 14px×2 */
const PAD_X = [0, 12, 0, 0, 0, 28, 0];
/** 줄 높이(글자 크기 배): 굵은 기울임 글꼴 + 외곽선의 실제 DOM 높이(38px 글자 → 55px, 2026-10-15 배치 계측) */
const LINE_EM = 1.45;
/** 예약 영역과 숫자 사이 최소 간격(px) */
const AVOID_GAP = 4;
/** 안전한 위치로 복귀하는 시간 상수(ms). 겹침을 피하는 필수 이동에는 적용하지 않는다. */
const AVOID_EASE_MS = 50;

/** Hit 이벤트 → 숫자 종류 */
export function hitKind(target: number, flags: number): number {
  if (target === 2) return K_HERO;
  if (flags & F_BLOCKED) return K_BLOCK;
  if (flags & F_CRIT) return K_CRIT;
  if (flags & F_MULTI) return K_MINOR;
  if (flags & F_AWAKEN) return K_AWAKEN;
  if (flags & (F_FINISHER | F_SKILL)) return K_BIG;
  return K_NORMAL;
}

const STYLE_ID = 'dh-dmg-style';
const CSS =
  '.dn{position:absolute;left:0;top:0;white-space:nowrap;will-change:transform,opacity;pointer-events:none;transform-origin:50% 100%;' +
  'font-family:Impact,"Arial Black","Malgun Gothic",sans-serif;font-weight:900;font-style:italic;display:none}' +
  '.dn-s{font-size:' + FONT_PX[K_MINOR] + 'px;color:#e8ebf6;-webkit-text-stroke:1px #1a2238;text-shadow:0 1px 0 #1a2238}' +
  '.dn-x{font-size:' + FONT_PX[K_BLOCK] + 'px;font-style:normal;font-family:system-ui,"Malgun Gothic",sans-serif;color:#c3c8d6;-webkit-text-stroke:.8px #1a2238;text-shadow:0 1px 0 #10162a;' +
  'padding:0 6px;border-radius:6px;background:rgba(40,46,64,.75);border:1px solid #8a92a8}' +
  '.dn-n{font-size:' + FONT_PX[K_NORMAL] + 'px;color:#fff;-webkit-text-stroke:1.2px #1a2238;text-shadow:0 2px 0 #1a2238}' +
  '.dn-k{font-size:' + FONT_PX[K_BIG] + 'px;color:#ffe9a8;-webkit-text-stroke:2px #4a2200;text-shadow:0 3px 0 #8a4a00,0 0 10px rgba(255,180,60,.75)}' +
  '.dn-a{font-size:' + FONT_PX[K_AWAKEN] + 'px;color:#fff;-webkit-text-stroke:2px #7a0b18;text-shadow:0 3px 0 #b3101f,0 0 14px rgba(255,60,80,.9)}' +
  '.dn-c{font-size:' + FONT_PX[K_CRIT] + 'px;color:#ffe45a;-webkit-text-stroke:2px #5a0a10;text-shadow:0 3px 0 #7a1018,0 0 14px rgba(255,60,40,.9);padding:0 14px;' +
  'background:linear-gradient(100deg,rgba(150,10,25,0),rgba(170,15,30,.65) 30%,rgba(170,15,30,.65) 70%,rgba(150,10,25,0))}' +
  // 치명타 라벨: 요소를 더하지 않고 의사 요소로 숫자 위 왼쪽에 작은 금빛 CRITICAL(풀 재사용 구조·DOM 수 불변)
  '.dn-c::before{content:"CRITICAL";position:absolute;left:12px;top:-18px;font-size:16px;font-style:italic;letter-spacing:1.5px;color:#ffe07a;-webkit-text-stroke:1px #4a0a10;' +
  'text-shadow:0 1px 0 #5a0a10,0 0 6px rgba(255,150,40,.8);transform:rotate(-4deg);transform-origin:0 100%;pointer-events:none}' +
  '.dn-h{font-size:' + FONT_PX[K_HERO] + 'px;color:#ff3b3b;-webkit-text-stroke:2px #2a0508;text-shadow:0 2px 0 #000,0 0 10px rgba(255,30,30,.7)}';

interface Entry {
  el: HTMLDivElement;
  active: boolean;
  age: number;
  /** QA 기록용: 이 숫자를 만든 타격의 틱 */
  tick: number;
  /** 이 숫자가 생긴 동작(group 호출) 번호. 같은 동작의 숫자는 새 숫자가 밀어내지 않는다 */
  born: number;
  x: number; y: number; z: number;
  value: number;
  kind: number;
  key: number;
  stack: number;
  /** 표시 글자 수(쓸 때만 갱신, 비키기 상자 폭 추정) */
  len: number;
  /** 예약 영역을 비킨 직전 방향(AVOID_*) */
  avoid: number;
  /** 지금 적용 중인 비키기 이동량(px). NaN = 아직 없음(처음 뜰 때는 바로 제자리) */
  ox: number;
  oy: number;
  /** 마지막으로 DOM에 쓴 양자화 값(바뀔 때만 쓴다): px, 배율×50, 불투명도×20 */
  lx: number;
  ly: number;
  ls: number;
  lo: number;
}

function fmt(n: number): string {
  const v = Math.round(n);
  if (v < 1000) return String(v);
  const s = String(v);
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ',';
    out += s[i];
  }
  return out;
}

export class DamageNumbers {
  private readonly pool: Entry[] = [];
  /** QA(?qa) 전용 결과 기록(타격마다 [틱, 대상, 인덱스, 결과, 값]). 정상 플레이에서는 null이라 비용이 없다 */
  private readonly qaLog: number[][] | null =
    typeof window !== 'undefined' && typeof location !== 'undefined' && new URLSearchParams(location.search).has('qa') ? [] : null;
  private curTick = 0;
  private groupSerial = 0;
  private readonly v = new THREE.Vector3();
  private readonly moved = new Float32Array(2);
  // 이번 프레임에 받은 Hit(고정 버퍼)
  private sN = 0;
  private readonly sTick = new Int32Array(STAGE_CAP);
  private readonly sTarget = new Int32Array(STAGE_CAP);
  private readonly sIndex = new Int32Array(STAGE_CAP);
  private readonly sKind = new Uint8Array(STAGE_CAP);
  private readonly sTaken = new Uint8Array(STAGE_CAP);
  private readonly sPos = new Float32Array(STAGE_CAP * 3);
  private readonly sValue = new Float32Array(STAGE_CAP);

  /** reserve: HUD 예약 영역(없으면 비키지 않는다) */
  constructor(layer: HTMLElement, private readonly reserve: ScreenReserve | null = null) {
    if (!document.getElementById(STYLE_ID)) {
      const st = document.createElement('style');
      st.id = STYLE_ID;
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    for (let i = 0; i < MAX_ON_SCREEN; i++) {
      const el = document.createElement('div');
      el.className = CLS[K_NORMAL];
      layer.appendChild(el);
      this.pool.push({ el, active: false, age: 0, tick: 0, born: 0, x: 0, y: 0, z: 0, value: 0, kind: K_NORMAL, key: -1, stack: 0, len: 0, avoid: AVOID_NONE, ox: NaN, oy: NaN, lx: 0, ly: 0, ls: -1, lo: -1 });
    }
    if (this.qaLog) (window as unknown as { __DN__: unknown }).__DN__ = { log: this.qaLog };
  }

  /** Hit 이벤트만 받는다. 실제 표시는 update에서 동작 단위로 묶어 정한다. */
  onEvent(e: GameEvent): void {
    if (e.type !== Ev.Hit) return;
    if (this.sN >= STAGE_CAP) this.flush();
    const i = this.sN++;
    this.sTick[i] = e.tick;
    this.sTarget[i] = e.b;
    this.sIndex[i] = e.c;
    this.sKind[i] = hitKind(e.b, e.flags);
    this.sValue[i] = e.a;
    this.sPos[i * 3] = e.x; this.sPos[i * 3 + 1] = e.y; this.sPos[i * 3 + 2] = e.z;
  }

  /** 쌓인 Hit를 틱(=동작)별로 묶어 띄운다 */
  private flush(): void {
    let a = 0;
    while (a < this.sN) {
      const t = this.sTick[a];
      let b = a + 1;
      while (b < this.sN && this.sTick[b] === t) b++;
      this.group(a, b);
      a = b;
    }
    this.sN = 0;
  }

  private group(a: number, b: number): void {
    this.groupSerial++;
    let mobHits = 0, blockShown = false;
    for (let k = a; k < b; k++) {
      this.sTaken[k] = 0;
      if (this.sTarget[k] !== 0) { this.spawnStaged(k); this.sTaken[k] = 1; continue; }
      if (this.sKind[k] === K_BLOCK) {
        if (!blockShown) { blockShown = true; this.spawnStaged(k); } else this.qa(this.sTick[k], this.sTarget[k] * 1000 + this.sIndex[k], 6, 0);
        this.sTaken[k] = 1;
        continue;
      }
      mobHits++;
    }
    if (mobHits <= MAX_ON_SCREEN) {
      for (let k = a; k < b; k++) if (!this.sTaken[k]) this.spawnStaged(k);
      return;
    }
    // 한 동작이 상한보다 많은 몹을 맞힌 경우만: 우선순위(치명·각성·스킬) → 값 순으로 상한까지
    for (let n = 0; n < MAX_ON_SCREEN; n++) {
      let best = -1, bestScore = -1;
      for (let k = a; k < b; k++) {
        if (this.sTaken[k]) continue;
        const score = PRIO[this.sKind[k]] * 1e9 + this.sValue[k];
        if (score > bestScore) { bestScore = score; best = k; }
      }
      if (best < 0) break;
      this.sTaken[best] = 1;
      this.spawnStaged(best);
    }
    if (this.qaLog) for (let k = a; k < b; k++) if (!this.sTaken[k]) this.qa(this.sTick[k], this.sTarget[k] * 1000 + this.sIndex[k], 2, this.sValue[k]);
  }

  private spawnStaged(k: number): void {
    const o = k * 3;
    this.curTick = this.sTick[k];
    this.spawn(this.sKind[k], this.sTarget[k] * 1000 + this.sIndex[k], this.sValue[k], this.sPos[o], this.sPos[o + 1], this.sPos[o + 2]);
  }

  /** QA 기록: 결과 0 새로 표시, 1 합산, 2 한 동작 상한으로 버림, 3 풀이 가득 차 버림, 4 밀려나 사라짐(값 = 나이 ms), 6 막힘 중복 */
  private qa(tick: number, key: number, outcome: number, value: number): void {
    if (this.qaLog && this.qaLog.length < 20000) this.qaLog.push([tick, Math.floor(key / 1000), key % 1000, outcome, Math.round(value)]);
  }

  private setKind(p: Entry, kind: number): void {
    if (p.kind !== kind) { p.kind = kind; p.el.className = CLS[kind]; }
  }

  private writeText(p: Entry): void {
    const s = p.kind === K_BLOCK ? BLOCK_TEXT : p.kind === K_HERO ? '-' + fmt(p.value) : fmt(p.value);
    p.el.textContent = s;
    p.len = s.length;
  }

  private spawn(kind: number, key: number, value: number, x: number, y: number, z: number): void {
    const pool = this.pool;
    // 같은 대상 MERGE_MS 안: 합친다(막힘끼리는 갱신만)
    for (let i = 0; i < pool.length; i++) {
      const p = pool[i];
      if (!p.active || p.key !== key || p.age >= MERGE_MS) continue;
      if ((p.kind === K_BLOCK) !== (kind === K_BLOCK)) continue;
      this.qa(this.curTick, key, 1, value);
      if (kind !== K_BLOCK) {
        p.value += value;
        if (PRIO[kind] > PRIO[p.kind]) this.setKind(p, kind);
        this.writeText(p);
      }
      p.age = 0;
      p.ls = -1;
      return;
    }
    // 빈 칸이 없으면 앞선 동작의 숫자 중 수명을 가장 많이 쓴 것(가장 오래된 것)을 지운다. 영웅 피격은 마지막까지 남긴다.
    let slot: Entry | null = null;
    for (let i = 0; i < pool.length; i++) if (!pool[i].active) { slot = pool[i]; break; }
    if (!slot) {
      let victim: Entry | null = null, vScore = -1;
      for (let i = 0; i < pool.length; i++) {
        const p = pool[i];
        if (p.born === this.groupSerial) continue;
        const score = (p.kind === K_HERO ? 0 : 10) + p.age / LIFE[p.kind];
        if (score > vScore) { vScore = score; victim = p; }
      }
      if (!victim) {
        // 전부 같은 동작의 숫자(영웅 피격 포함): 우선순위가 낮은 것만 더 중요한 새 숫자로 바꾼다
        for (let i = 0; i < pool.length; i++) {
          const p = pool[i];
          if (!victim || PRIO[p.kind] < PRIO[victim.kind] || (PRIO[p.kind] === PRIO[victim.kind] && p.age > victim.age)) victim = p;
        }
        if (!victim || PRIO[victim.kind] >= PRIO[kind]) { this.qa(this.curTick, key, 3, value); return; }
      }
      this.qa(victim.tick, victim.key, 4, victim.age);
      slot = victim;
    }
    let stack = 0;
    for (let i = 0; i < pool.length; i++) {
      const p = pool[i];
      if (p !== slot && p.active && p.key === key && p.age < STACK_WINDOW_MS) stack++;
    }
    slot.active = true;
    slot.age = 0;
    slot.tick = this.curTick;
    slot.born = this.groupSerial;
    this.qa(this.curTick, key, 0, value);
    slot.x = x; slot.y = y; slot.z = z;
    slot.value = value;
    slot.key = key;
    slot.stack = stack;
    slot.avoid = AVOID_NONE;
    slot.ox = slot.oy = NaN;
    this.setKind(slot, kind);
    this.writeText(slot);
    slot.el.style.display = 'block';
    slot.el.style.opacity = '0';
    slot.lx = slot.ly = -99999;
    slot.ls = slot.lo = -1;
  }

  /** 매 프레임. dtMs = 프레임 경과 ms, width/height = 화면 크기(px). */
  update(dtMs: number, camera: THREE.Camera, width: number, height: number): void {
    if (this.sN > 0) this.flush();
    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      if (!p.active) continue;
      p.age += dtMs;
      const life = LIFE[p.kind];
      if (p.age >= life) {
        p.active = false;
        p.el.style.display = 'none';
        continue;
      }
      const t = p.age / life;
      const rise = DAMAGE_NUMBERS.riseM * RISE[p.kind] * (1 - (1 - t) * (1 - t));
      this.v.set(p.x, p.y + rise + 1.6, p.z).project(camera);
      const behind = this.v.z > 1;
      let sx = Math.round((this.v.x * 0.5 + 0.5) * width + p.stack * 24);
      let sy = Math.round((-this.v.y * 0.5 + 0.5) * height - p.stack * 22);
      // 등장 펀치(90ms): 큰 숫자 1.4배 → 1.0, 일반은 거의 없음
      const ls = p.age < 90 ? Math.round(50 + PUNCH[p.kind] * (1 - p.age / 90)) : 50;
      if (this.reserve && !behind) {
        // HUD 예약 영역을 비킨다: 상자 크기는 이 파일의 글자 크기 표에서 추정(DOM 크기를 읽지 않는다)
        const k = p.kind, sc = ls / 50, fpx = FONT_PX[k];
        const w = (p.len * fpx * CHAR_EM[k] + PAD_X[k]) * sc, h = fpx * LINE_EM * sc;
        p.avoid = avoidReserve(this.reserve, sx, sy, w, h, AVOID_GAP, width, height, p.avoid, this.moved);
        const tx = this.moved[0] - sx, ty = this.moved[1] - sy;
        if (Number.isNaN(p.ox)) { p.ox = tx; p.oy = ty; }
        else { const e = 1 - Math.exp(-dtMs / AVOID_EASE_MS); p.ox += (tx - p.ox) * e; p.oy += (ty - p.oy) * e; }
        const ex = Math.round(sx + p.ox), ey = Math.round(sy + p.oy);
        const wasInside = sx - w / 2 >= 0 && sx + w / 2 <= width && sy - h >= 0 && sy <= height;
        const leavesScreen = wasInside && (ex - w / 2 < 0 || ex + w / 2 > width || ey - h < 0 || ey > height);
        // 필수 회피는 즉시 적용한다. 보간은 겹침·새 화면 이탈이 없는 복귀 구간에만 허용한다.
        // 원래 카메라 밖인 숫자는 화면 안으로 고정하지 않는다.
        // 정수 px 반올림 오차 1px만 허용하며 실제 HUD와는 여유를 남긴다.
        if (leavesScreen || overlapsReserve(this.reserve, ex, ey, w, h, AVOID_GAP - 1)) {
          p.ox = tx; p.oy = ty;
        }
        sx = Math.round(sx + p.ox);
        sy = Math.round(sy + p.oy);
      }
      const lo = behind ? 0 : t > 0.6 ? Math.round((1 - (t - 0.6) / 0.4) * 20) : 20;
      if (lo !== p.lo) {
        p.lo = lo;
        p.el.style.opacity = OPACITY[lo];
      }
      if (sx !== p.lx || sy !== p.ly || ls !== p.ls) {
        p.lx = sx;
        p.ly = sy;
        p.ls = ls;
        p.el.style.transform = 'translate3d(' + sx + 'px,' + sy + 'px,0) translate(-50%,-100%) scale(' + ls / 50 + ')';
      }
    }
  }

  get activeCount(): number {
    let n = 0;
    for (let i = 0; i < this.pool.length; i++) if (this.pool[i].active) n++;
    return n;
  }

  clear(): void {
    this.sN = 0;
    for (const p of this.pool) { p.active = false; p.el.style.display = 'none'; }
  }

  dispose(): void {
    for (const p of this.pool) p.el.remove();
    this.pool.length = 0;
  }
}
